<script setup lang="ts">
import {
  getCategoryRoute,
  getTranslatedProperty,
} from "@shopware/helpers";
import type { Schemas } from "#shopware";
import { onKeyStroke, useActiveElement } from "@vueuse/core";
type NavigationElement = Schemas["Category"] & {
  activeClass?: boolean;
  isDropdownOpen?: boolean;
};

const { navigationElements } = useNavigation();
const currentMenuPosition = ref<string | undefined>(undefined);
const resetActiveClass = ref<boolean>(true);

const route = useRoute();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

// Mobile dropdown state management
const openDropdownId = ref<string | null>(null);
const componentKey = ref(1);

const toggleDropdown = (navigationId: string) => {
  if (openDropdownId.value === navigationId) {
    openDropdownId.value = null;
  } else {
    openDropdownId.value = navigationId;
  }
};

// Function to handle mobile link clicks
const handleMobileLinkClick = (navigationId: string, parentId?: string) => {
  updateActiveClass(navigationId, parentId);
  // Close dropdown on mobile when any link is clicked
  openDropdownId.value = null;
  
  // Close mobile navigation menu on mobile devices
  if (window.innerWidth <= 992) {
    // Find and close the mobile menu by triggering the navbar-toggler click
    const navbarToggler = document.querySelector('.navbar-toggler') as HTMLButtonElement;
    if (navbarToggler && navbarToggler.classList.contains('open')) {
      navbarToggler.click();
    }
  }
};

const isDropdownOpen = (navigationId: string) => {
  return openDropdownId.value === navigationId;
};

onMounted(() => {
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.dropdown-submenu')) {
      openDropdownId.value = null;
    }
  });
  let currentNaviagtionElement: NavigationElement | undefined;
  if (navigationElements.value) {
    currentNaviagtionElement = findNavigationElement(
      route.path.slice(1),
      navigationElements.value
    );
  }
  if (currentNaviagtionElement) {
    updateActiveClass(
      currentNaviagtionElement.id,
      currentNaviagtionElement.parentId
    );
  }
});

const findNavigationElement = (
  routePath: string,
  navigation: NavigationElement[]
): NavigationElement | undefined => {
  for (let i = 0; i < navigation.length; i++) {
    const navigationElement = navigation[i];
    const seoUrls = navigationElement.seoUrls as
      | Schemas["SeoUrl"][]
      | undefined;
    if (seoUrls) {
      for (let j = 0; j < seoUrls.length; j++) {
        const currentSeoUrl = seoUrls[j];
        if (currentSeoUrl && currentSeoUrl.seoPathInfo === routePath) {
          return navigationElement;
        }
      }
    } else if (navigationElement.externalLink === routePath) {
      return navigationElement;
    }
    const children = navigationElement.children;
    if (children) {
      const foundElement = findNavigationElement(routePath, children);
      if (foundElement) {
        return foundElement;
      }
    }
  }
  return undefined;
};

const onUpdateActiveClass = (
  navigationId: string,
  parentId: string | undefined
) => {
  updateActiveClass(navigationId, parentId);
};

const resetNavigationActiveClass = (navigation: NavigationElement[]) => {
  for (let ni = 0; ni < navigation.length; ++ni) {
    navigation[ni].activeClass = false;
    const children = navigation[ni].children;
    if (children) {
      resetNavigationActiveClass(children);
    }
  }
};

const updateActiveClass = (
  navigationId: string,
  parentId: string | undefined
) => {
  const setNavigationActiveClass = (
    navigation: NavigationElement[],
    navigationId: string,
    parentId: string | undefined
  ) => {
    for (let ni = 0; ni < navigation.length; ++ni) {
      if (navigation[ni].id === navigationId) {
        navigation[ni].activeClass = true;
      }
      if (navigation[ni].id == parentId) {
        navigation[ni].activeClass = true;
        if (navigationElements.value) {
          setNavigationActiveClass(
            navigationElements.value,
            navigationId,
            navigation[ni].parentId
          );
        }
      }
      const children = navigation[ni].children;
      if (children) {
        setNavigationActiveClass(children, navigationId, parentId);
      }
    }
  };

  if (navigationElements.value) {
    resetNavigationActiveClass(navigationElements.value);
    setNavigationActiveClass(navigationElements.value, navigationId, parentId);
    resetActiveClass.value = false;
  }
};
//#region - keyboard navigation (highliy connected to the HTML structure)
const activeElement = useActiveElement();
onKeyStroke("ArrowDown", (e) => {
  // next parent link or next child link
  if (
    activeElement.value?.parentElement?.nextElementSibling?.getElementsByTagName(
      "a"
    )[0]
  ) {
    e.preventDefault();
    activeElement.value.parentElement.nextElementSibling
      .getElementsByTagName("a")[0]
      .focus();
    return;
  }
});

onKeyStroke("ArrowUp", (e) => {
  // previous parent link or previous child link
  if (
    activeElement.value?.parentElement?.previousElementSibling?.getElementsByTagName(
      "a"
    )[0]
  ) {
    e.preventDefault();
    activeElement.value?.parentElement.previousElementSibling
      .getElementsByTagName("a")[0]
      .focus();
    return;
  }
});

function isFocusInsideNav() {
  if (activeElement.value) {
    const focusInsideMenu = document
      .getElementsByTagName("nav")[0]
      .contains(activeElement.value);
    return focusInsideMenu;
  }
  return false;
}

onKeyStroke("Escape", () => {
  if (isFocusInsideNav()) {
    currentMenuPosition.value = undefined;
    document
      .getElementsByTagName("header")[0]
      .getElementsByTagName("a")[0]
      .focus();
  }
});
//#endregion - keyboard navigation

// reset when route.path changes
watch(
  () => route.path,
  () => {
    if (resetActiveClass.value == true && navigationElements.value) {
      resetNavigationActiveClass(navigationElements.value);
    }
    resetActiveClass.value = true;
    currentMenuPosition.value = undefined;
    // Close mobile dropdown when route changes
    openDropdownId.value = null;
    componentKey.value++;
    
    // Close mobile navigation menu when route changes
    if (window.innerWidth <= 992) {
      const navbarToggler = document.querySelector('.navbar-toggler') as HTMLButtonElement;
      if (navbarToggler && navbarToggler.classList.contains('open')) {
        navbarToggler.click();
      }
    }
  }
);
</script>

<template>
  <!-- eslint-disable vue/no-v-html -->
  <div>
    <ul class="navbar-nav ms-auto mb-2 mb-lg-0">
      <li
        v-for="(navigationElement, index) in navigationElements"
        :key="navigationElement.id+componentKey"
        :class="{
          'nav-item': true,
          current: (navigationElement as NavigationElement).activeClass,
          'dropdown dropdown-submenu': navigationElement.children.length > 0,
          'active': isDropdownOpen(navigationElement.id)
        }"
      >
        <NuxtLink
          v-if="navigationElement.type !== 'folder'"
          role="menuitem"
          :class="{
            'nav-link': true,
            'dropdown-toggle !flex items-center justify-between': navigationElement.children.length > 0,
          }"
          :target="
            navigationElement.externalLink && navigationElement.linkNewTab
              ? '_blank'
              : ''
          "
          :to="formatLink(getCategoryRoute(navigationElement))"
          @click="handleMobileLinkClick(navigationElement.id, navigationElement.parentId)"
        >
          {{ getTranslatedProperty(navigationElement, "name") }}
          <span v-if="navigationElement.children.length > 0" class="dropdown-arrow">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M6 9L12 15L18 9" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </NuxtLink>
        <a
          v-else
          href="javascript:void(0)"
          :class="{
            'nav-link': true,
            'dropdown-toggle !flex items-center justify-between': navigationElement.children.length > 0,
          }"
          @click="toggleDropdown(navigationElement.id)"
          >{{ getTranslatedProperty(navigationElement, "name") }}
          <span v-if="navigationElement.children.length > 0" class="dropdown-arrow">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M6 9L12 15L18 9" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </a>
        <ul
          v-if="navigationElement.children.length > 0"
          :class="{
            'dropdown-menu': true,
            'show': isDropdownOpen(navigationElement.id)
          }"
        >
          <li
            v-for="(childElement, index) in navigationElement.children"
            :key="childElement.id"
          >
            <NuxtLink
              role="menuitem"
              :class="{ 'dropdown-item': true }"
              :target="
                childElement.externalLink && childElement.linkNewTab
                  ? '_blank'
                  : ''
              "
              :to="formatLink(getCategoryRoute(childElement))"
              @click="handleMobileLinkClick(childElement.id, childElement.parentId)"
            >
              {{ getTranslatedProperty(childElement, "name") }}
            </NuxtLink>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>
